import "./bootstrap";
import {
    Livewire,
    Alpine,
} from "../../vendor/livewire/livewire/dist/livewire.esm";
import flatpickr from "flatpickr";
import "./img-preview";
import { registerToastStore } from "./toast";
import { registerAsyncPicker } from "./components/async-picker";
import { registerLivewireHtmlForm } from "./components/livewire-html-form";
import { registerZeroValueLineTracker } from "./components/zero-value-line-tracker";
import { registerRevenueByChannelChart } from "./components/revenue-by-channel-chart";
import { registerRevenueTrendChart } from "./components/revenue-trend-chart";
import { registerForecastTrendChart } from "./components/forecast-trend-chart";
import { registerWarehouseTrendChart } from "./components/warehouse-trend-chart";
import { registerCommissionOverridePolicies } from "./components/commission-override-policies";
import { registerCreditNoteLineSelector } from "./components/credit-note-line-selector";
import { registerFileUploader } from "./components/file-uploader";
import { registerFileStatusMonitor } from "./components/file-status-monitor";
import { registerProductDevelopmentActivityStreamMonitor } from "./components/product-development-activity-stream-monitor";
import { registerShopifyVariantMappingTable } from "./components/shopify-variant-mapping-table";
import { registerDateRangePicker } from "./components/date-range-picker";
import { registerImmediateFilterForm } from "./components/immediate-filter-form";
import { registerSearchableSelect } from "./components/searchable-select";
import { registerSidebarMenu } from "./components/sidebar-menu";
import { registerRoyaltyTemplateBuilder } from "./components/royalty-template-builder";
import { registerConfirmDialog } from "./components/confirm-dialog";
import { registerOpportunityStagePath } from "./components/opportunity-stage-path";
import { registerMyWorkQueue } from "./components/my-work-queue";
import { registerPageDrawer } from "./components/page-drawer";

window.flatpickr = flatpickr;

const desktopSidebarMediaQuery = "(min-width: 1280px)";
const sidebarCollapsedStorageKey = "sidebar-collapsed";
const systemThemeMediaQuery = "(prefers-color-scheme: dark)";
const themePreferences = ["auto", "light", "dark"];
const themeStorageKey = "theme";

const sidebarIsCollapsed = () => {
    try {
        return window.localStorage.getItem(sidebarCollapsedStorageKey) === "true";
    } catch {
        return false;
    }
};

const storedThemePreference = () => {
    try {
        const storedTheme = window.localStorage.getItem(themeStorageKey);

        return themePreferences.includes(storedTheme) && storedTheme !== "auto"
            ? storedTheme
            : "auto";
    } catch {
        return "auto";
    }
};

Alpine.data("appLayout", () => ({
    loading: true,
    themePreference: storedThemePreference(),
    darkMode: document.documentElement.classList.contains("dark"),
    sidebarToggle:
        window.matchMedia(desktopSidebarMediaQuery).matches && sidebarIsCollapsed(),
    init() {
        const desktopSidebar = window.matchMedia(desktopSidebarMediaQuery);
        const systemTheme = window.matchMedia(systemThemeMediaQuery);

        this.$watch("themePreference", (themePreference) => {
            this.applyThemePreference(themePreference, systemTheme.matches);

            try {
                if (themePreference === "auto") {
                    window.localStorage.removeItem(themeStorageKey);
                } else {
                    window.localStorage.setItem(themeStorageKey, themePreference);
                }
            } catch {}
        });

        this.$watch("sidebarToggle", (collapsed) => {
            if (!desktopSidebar.matches) {
                return;
            }

            try {
                window.localStorage.setItem(
                    sidebarCollapsedStorageKey,
                    String(collapsed),
                );
            } catch {}

            document.documentElement.dataset.sidebarCollapsed = String(collapsed);
        });

        desktopSidebar.addEventListener("change", (event) => {
            this.sidebarToggle = event.matches && sidebarIsCollapsed();
        });

        systemTheme.addEventListener("change", (event) => {
            if (this.themePreference === "auto") {
                this.applyThemePreference("auto", event.matches);
            }
        });
    },
    applyThemePreference(themePreference, systemPrefersDark) {
        const isDarkMode =
            themePreference === "dark" ||
            (themePreference === "auto" && systemPrefersDark);

        this.darkMode = isDarkMode;
        document.documentElement.classList.toggle("dark", isDarkMode);
        document.documentElement.style.colorScheme = isDarkMode
            ? "dark"
            : "light";
    },
    setThemePreference(themePreference) {
        if (themePreferences.includes(themePreference)) {
            this.themePreference = themePreference;
        }
    },
    themeToggleLabel() {
        const currentLabel =
            this.themePreference.charAt(0).toUpperCase() +
            this.themePreference.slice(1);

        return `Choose theme. Current: ${currentLabel}`;
    },
}));

registerToastStore(Alpine);
registerAsyncPicker(Alpine);
registerLivewireHtmlForm(Alpine);
registerZeroValueLineTracker(Alpine);
registerRevenueByChannelChart(Alpine);
registerRevenueTrendChart(Alpine);
registerForecastTrendChart(Alpine);
registerWarehouseTrendChart(Alpine);
registerCommissionOverridePolicies(Alpine);
registerCreditNoteLineSelector(Alpine);
registerFileUploader(Alpine);
registerFileStatusMonitor(Alpine);
registerProductDevelopmentActivityStreamMonitor(Alpine);
registerShopifyVariantMappingTable(Alpine);
registerDateRangePicker(Alpine);
registerImmediateFilterForm(Alpine);
registerSearchableSelect(Alpine);
registerSidebarMenu(Alpine);
registerRoyaltyTemplateBuilder(Alpine);
registerConfirmDialog(Alpine);
registerOpportunityStagePath(Alpine);
registerMyWorkQueue(Alpine);
registerPageDrawer(Alpine);

Alpine.data("datepicker", ({ model, initial, withTime, live = true, showToday = false, todayLabel = "Today", autosubmit = false, trigger = false, allowWeekends = true, appendTo = null }) => ({
    instance: null,
    value: null,
    init() {
        if (model) {
            this.$wire.entangle(model, live);
            this.value = this.$wire.get(model);
        } else {
            this.value = initial ?? null;
        }

        const displayFormat = "j M Y" + (withTime ? " H:i" : "");
        const dateFormat = "Y-m-d" + (withTime ? "\\TH:i" : "");
        const flatpickrOptions = {
            enableTime: withTime || false,
            altInput: !trigger,
            altFormat: displayFormat,
            dateFormat: dateFormat,
            defaultDate: model ? this.$wire.get(model) : initial || null,
            onReady: (selected, dateStr, instance) => {
                if (instance.altInput) {
                    ["aria-invalid", "aria-describedby"].forEach((attribute) => {
                        const value = instance.input.getAttribute(attribute);

                        if (value !== null) {
                            instance.altInput.setAttribute(attribute, value);
                        }
                    });
                }

                if (showToday) {
                    this.addTodayButton(instance);
                }
            },
            onChange: (selected, dateStr) => {
                this.value = dateStr || null;
                if (model) {
                    this.$wire.set(model, this.value, live);
                }
                this.submitForm();
            },
        };

        if (!allowWeekends) {
            flatpickrOptions.disable = [
                (date) => date.getDay() === 0 || date.getDay() === 6,
            ];
        }

        const appendTarget = appendTo ? this.$root.closest(appendTo) : null;

        if (appendTarget) {
            flatpickrOptions.appendTo = appendTarget;
        }

        this.instance = flatpickr(this.$refs.input, flatpickrOptions);

        if (model) {
            this.$watch("value", (v) => {
                if (!this.instance) {
                    return;
                }

                const current = this.instance.input.value || null;
                if (current !== (v || null)) {
                    this.instance.setDate(v || null, false);
                }
            });
        }
    },
    addTodayButton(instance) {
        const footer = document.createElement("div");
        footer.className = "border-t border-gray-200 p-2 text-right dark:border-gray-700";

        const button = document.createElement("button");
        button.type = "button";
        button.className = "rounded-md px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10";
        button.textContent = todayLabel;
        button.addEventListener("click", () => {
            instance.setDate(new Date(), true);
            instance.close();
        });

        footer.appendChild(button);
        instance.calendarContainer.appendChild(footer);
    },
    reset() {
        this.instance.clear();
    },
    open() {
        this.instance.open();
    },
    submitForm() {
        if (!autosubmit) {
            return;
        }

        this.$nextTick(() => {
            const form = this.$refs.input?.form;

            if (form) {
                form.requestSubmit();
            }
        });
    },
}));

Alpine.data(
    "rowLink",
    ({ href = null, enabled = true, newTab = false } = {}) => ({
        initialHref: href,
        enabled,
        newTab,
        get destination() {
            const elementHref = this.$el?.dataset?.rowLinkHref;
            if (elementHref !== undefined) {
                return elementHref || null;
            }
            return this.initialHref;
        },
        get canNavigate() {
            return Boolean(this.enabled && this.destination);
        },
        shouldIgnore(event) {
            return Boolean(
                event.target.closest(
                    "a, button, input, textarea, select, [data-row-action-ignore]",
                ),
            );
        },
        handleClick(event) {
            if (
                !this.canNavigate ||
                event.defaultPrevented ||
                this.shouldIgnore(event)
            ) {
                return;
            }
            const selection = window.getSelection?.().toString() ?? "";
            if (selection.length > 0) {
                return;
            }
            if (event.button !== undefined && event.button !== 0) {
                return;
            }
            this.open(event);
        },
        handleKey(event) {
            if (
                !this.canNavigate ||
                event.defaultPrevented ||
                this.shouldIgnore(event)
            ) {
                return;
            }
            this.open(event);
        },
        handleAux(event) {
            if (!this.canNavigate || this.shouldIgnore(event)) {
                return;
            }
            if (event.button === 1) {
                event.preventDefault();
                this.openInNewTab();
            }
        },
        open(event) {
            if (!this.canNavigate) {
                return;
            }
            if (this.newTab) {
                this.openInNewTab();
                return;
            }
            if (event.metaKey || event.ctrlKey || event.shiftKey) {
                this.openInNewTab();
                return;
            }
            window.location.assign(this.destination);
        },
        openInNewTab() {
            if (!this.canNavigate) {
                return;
            }
            window.open(this.destination, "_blank", "noopener");
        },
    }),
);

Alpine.data(
    "conceptStageComposer",
    ({
        composerOpen = false,
        stageId = "",
        initialStageId = "",
        initialStageNote = "",
    } = {}) => ({
        composerOpen,
        stageId,
        initialStageId,
        stageNote: initialStageNote,
        handleStageChange() {
            if (this.stageId === this.initialStageId) {
                this.stageNote = initialStageNote;
                this.composerOpen = false;
                return;
            }

            this.composerOpen = true;
            this.$nextTick(() => this.$refs.stageNote?.focus());
        },
        cancel() {
            this.stageId = this.initialStageId;
            this.stageNote = initialStageNote;
            this.composerOpen = false;
        },
    }),
);

Alpine.data(
    "conceptActivityComposer",
    ({
        composerOpen = false,
        composerNote = "",
        markAsDone = false,
        canMarkDone = false,
        hasPostedUpdateInActiveIteration = false,
        markDoneBlockedReason = null,
        draftDiscardMessage = "Discard this update draft?",
        uploadsPendingMessage = "Please wait for attachment uploads to finish before cancelling.",
        uploadDiscardFailedMessage = "Unable to discard attachments right now.",
        markDoneValidationMessage = "Add an update before marking this concept done.",
    } = {}) => ({
        composerOpen,
        composerNote,
        markAsDone,
        canMarkDone,
        hasPostedUpdateInActiveIteration,
        markDoneBlockedReason,
        draftDiscardMessage,
        uploadsPendingMessage,
        uploadDiscardFailedMessage,
        markDoneValidationMessage,
        composerDragActive: false,
        uploaderState: {
            hasUploadsInFlight: false,
            uploading: false,
            filesCount: 0,
        },
        uploaderApi: null,
        windowDragEnterHandler: null,
        windowDragOverHandler: null,
        windowDragLeaveHandler: null,
        windowDropHandler: null,
        documentDropHandler: null,
        documentDragEndHandler: null,
        init() {
            this.windowDragEnterHandler = (event) => {
                if (!this.composerOpen || !this.eventHasFiles(event)) {
                    return;
                }

                event.preventDefault();
                this.composerDragActive = true;
            };

            this.windowDragOverHandler = (event) => {
                if (!this.composerOpen || !this.eventHasFiles(event)) {
                    return;
                }

                event.preventDefault();
                this.composerDragActive = true;

                if (event.dataTransfer) {
                    event.dataTransfer.dropEffect = "copy";
                }
            };

            this.windowDragLeaveHandler = (event) => {
                if (!this.composerOpen) {
                    return;
                }

                const leftWindow =
                    event.clientX <= 0 ||
                    event.clientY <= 0 ||
                    event.clientX >= window.innerWidth ||
                    event.clientY >= window.innerHeight;

                if (leftWindow) {
                    this.resetComposerDragState();
                }
            };

            this.windowDropHandler = (event) => {
                if (!this.composerOpen || !this.eventHasFiles(event)) {
                    return;
                }

                event.preventDefault();
                this.resetComposerDragState();
            };

            this.documentDropHandler = () => {
                this.resetComposerDragState();
            };

            this.documentDragEndHandler = () => {
                this.resetComposerDragState();
            };

            window.addEventListener("dragenter", this.windowDragEnterHandler);
            window.addEventListener("dragover", this.windowDragOverHandler);
            window.addEventListener("dragleave", this.windowDragLeaveHandler);
            window.addEventListener("drop", this.windowDropHandler);
            document.addEventListener("drop", this.documentDropHandler);
            document.addEventListener("dragend", this.documentDragEndHandler);
        },
        destroy() {
            if (this.windowDragEnterHandler) {
                window.removeEventListener("dragenter", this.windowDragEnterHandler);
            }

            if (this.windowDragOverHandler) {
                window.removeEventListener("dragover", this.windowDragOverHandler);
            }

            if (this.windowDragLeaveHandler) {
                window.removeEventListener("dragleave", this.windowDragLeaveHandler);
            }

            if (this.windowDropHandler) {
                window.removeEventListener("drop", this.windowDropHandler);
            }

            if (this.documentDropHandler) {
                document.removeEventListener("drop", this.documentDropHandler);
            }

            if (this.documentDragEndHandler) {
                document.removeEventListener("dragend", this.documentDragEndHandler);
            }
        },
        async cancel() {
            if (this.uploaderState.hasUploadsInFlight) {
                window.alert(this.uploadsPendingMessage);
                return;
            }

            if (
                this.composerNote.trim().length > 30 &&
                !window.confirm(this.draftDiscardMessage)
            ) {
                return;
            }

            if (this.uploaderApi?.abandonUploads) {
                try {
                    await this.uploaderApi.abandonUploads();
                } catch (error) {
                    window.alert(
                        error instanceof Error
                            ? error.message
                            : this.uploadDiscardFailedMessage,
                    );
                    return;
                }
            }

            this.composerNote = "";
            this.markAsDone = false;
            this.resetComposerDragState();
            this.composerOpen = false;
        },
        openComposer() {
            this.composerOpen = true;
            this.$nextTick(() => this.$refs.composerTextarea?.focus());
        },
        eventHasFiles(event) {
            const types = event.dataTransfer?.types;

            if (!types) {
                return false;
            }

            return Array.from(types).includes("Files");
        },
        handleComposerDragOver(event) {
            if (!this.eventHasFiles(event)) {
                return;
            }

            event.preventDefault();
            this.composerDragActive = true;

            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = "copy";
            }
        },
        handleComposerDrop(event) {
            if (!this.eventHasFiles(event)) {
                return;
            }

            event.preventDefault();
            this.resetComposerDragState();
            this.uploaderApi?.addFiles?.(event.dataTransfer?.files ?? []);
        },
        resetComposerDragState() {
            this.composerDragActive = false;
        },
        canEnableMarkDone() {
            return this.canMarkDone && !this.markDoneBlockedReason && (
                this.composerNote.trim().length > 0 ||
                this.uploaderState.filesCount > 0 ||
                this.hasPostedUpdateInActiveIteration
            );
        },
        markDoneDisabledReason() {
            if (this.markDoneBlockedReason) {
                return this.markDoneBlockedReason;
            }

            if (!this.canMarkDone || this.canEnableMarkDone()) {
                return "";
            }

            return this.markDoneValidationMessage;
        },
        submitComposer(form) {
            if (this.uploaderState.hasUploadsInFlight) {
                return;
            }

            if (this.markAsDone && !this.canEnableMarkDone()) {
                window.alert(this.markDoneDisabledReason() || this.markDoneValidationMessage);
                return;
            }

            form.submit();
        },
    }),
);

window.Livewire = Livewire;

Livewire.start();
